import reportReasons from '@apis/reportReasons' // Fixed the import name (was reportReasons)
import { deletesvg, edit, outlinenotification } from '@assets/icons'
import Table from '@components/table'
import Wrapper from '@components/wrapper'
import useScrollToTop from '@hooks/useScrollToTop'
import { RootState } from '@store/index'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import Slider from 'react-slick'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'
import { ImagePlus, Plus } from 'lucide-react'
import { useModal } from '@context/ModalContext'
import styled from '@emotion/styled'
import {
  Body,
  Button,
  Container,
  FileInput,
  Footer,
  Header,
  ImagePreview,
  UploadArea,
  UploadLabel,
} from './style'

const ReportReasonList = ({ onEdit }) => {
  const { showModal } = useModal()
  const scrollToTop = useScrollToTop()
  const {
    items: reportReason,
    totalItems,
    totalPages,
    loading,
  } = useSelector((state: RootState) => state.reportReason)

  const [page, setPage] = useState(1)
  const fetchReportReason = async () => {
    try {
      await reportReasons.list({
        state: 'reportReason',
        params: {
          page,
          limit: 30,
        },
      })
    } catch (error) {
      console.error('Error fetching report reasons:', error)
    }
  }

  const columns = [
    { title: 'Game Name', key: 'gameName' },
    { title: 'User Role', key: 'userRole' },
    { title: 'Report Reason', key: 'reportReason' },
    {
      title: 'Reference Image',
      key: 'referenceImage',
      render: (item) => {
        const images = item.referenceImage || []

        if (images.length === 0) {
          return <span>No images</span>
        }

        const settings = {
          dots: false,
          infinite: images.length > 1,
          speed: 300,
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: true,
          adaptiveHeight: true,
        }

        return (
          <div
            style={{
              width: '120px',
              maxWidth: '100%',
              margin: '0 auto',
              padding: '4px 0',
            }}
          >
            <Slider {...settings}>
              {images.map((img, idx) => (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '70px',
                  }}
                >
                  <img
                    src={`https://admin.tournaop.com/${img}`}
                    alt={`ref-${idx}`}
                    style={{
                      width: '100px',
                      height: '100px',
                      objectFit: 'contain',
                      borderRadius: '4px',
                      margin: '0 auto',
                      display: 'block',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                      background: '#f8f8f8',
                    }}
                  />
                </div>
              ))}
            </Slider>
            <style>{`
              .slick-dots li button:before {
                color: #333 !important;
                font-size: 10px !important;
                opacity: 0.7;
              }
              .slick-dots li.slick-active button:before {
                color: #1976d2 !important;
                opacity: 1;
              }
              .slick-arrow {
                z-index: 9999999999;
               
              }
            `}</style>
          </div>
        )
      },
    },
  ]

  const data = reportReason.map((rr) => ({
    id: rr.id,
    gameName: rr.name,
    userRole: rr.role,
    referenceImage: rr.images,
    reportReason: rr.reason,
  }))

  useEffect(() => {
    fetchReportReason()
  }, [page])

  return (
    <Wrapper>
      <Table
        loading={loading}
        text="Report Reason Lists"
        icon={outlinenotification}
        searchKeys={['gameName']}
        columns={columns}
        data={data}
        onPageChange={setPage}
        totalItems={totalItems}
        totalPages={totalPages}
        pageControlled={page}
        actions={[
          {
            icon: edit,
            key: 'edit',
            onClick: (item) => {
              onEdit(item)
              scrollToTop()
            },
          },
          {
            icon: deletesvg,
            key: 'delete',
            onClick: async (item) => {
              await reportReasons.remove({ state: 'reportReason', id: item.id })
            },
          },
          {
            icon: <ImagePlus />,
            key: 'edit',
            onClick: async (item) => {
              showModal(<AddImageModal item={item} />)
            },
          },
        ]}
      />
    </Wrapper>
  )
}

export default ReportReasonList

export const AddImageModal = ({ item }: any) => {
  const { hideModal } = useModal()
  const [imageUrl, setImageUrl] = useState('')
  const [isUpdating, setIsUpdating] = useState(false)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (event) => {
        setImageUrl(event.target?.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleUpdate = async () => {
    setIsUpdating(true)
    const updateData = {
      name: item.gameName || 'Pubg',
      reason: item.reportReason,
      role: item.userRole || 'Joiner',
      images: [
        ...(item.referenceImage || []),
        {
          base64: imageUrl.split(',')[1] || '',
          extension: imageUrl.split(';')[0].split('/')[1] || '',
        },
      ],
    }

    await reportReasons.update({
      state: 'reportReason',
      id: item.id,
      data: updateData,
    })
    hideModal()
  }

  return (
    <Container>
      <Header>
        <h3>Add Image for {item?.gameName || 'Game'}</h3>
      </Header>

      <Body>
        {imageUrl ? (
          <ImagePreview src={imageUrl} alt="Preview" />
        ) : (
          <UploadArea>
            <FileInput
              type="file"
              id="image-upload"
              accept="image/*"
              onChange={handleFileChange}
            />
            <UploadLabel htmlFor="image-upload">
              <span>📁</span>
              <p>Click to upload image</p>
            </UploadLabel>
          </UploadArea>
        )}
      </Body>

      <Footer>
        <Button onClick={handleUpdate} disabled={!imageUrl || isUpdating}>
          {isUpdating ? 'Updating...' : 'Update'}
        </Button>
      </Footer>
    </Container>
  )
}
